import React, { useMemo, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import {
  getReporters,
  getSubmission,
} from 'store/entities/submissionDetail/thunks';
import { changeObligationReporter } from 'store/entities/submissionDetail/thunks';
import { toggleChangeReporterModal } from 'store/entities/submissionDetail';
import { notifySuccess } from 'utils/notifications';

import Button from 'components/UIComponents/Button';
import { Column, Row, Section } from 'components/UIComponents/Layout';
import Modal, { ModalCard } from 'components/UIComponents/Modal';
import { Field, Label, Select, Control } from 'components/UIComponents/Form';
import ValidationError from 'components/FormControls/ValidationError';

export default props => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const {
    submissionDetail: { obligation = {}, id: submissionId },
    reporters,
    reportersLoading,
    changeReporterModalOpen,
    changeObligationReporterSubmitting,
  } = useSelector(state => state.submissionDetail);

  const loadingState = reportersLoading || changeObligationReporterSubmitting;
  const { institutionDto, tableDto, id: obligationId } = obligation;
  const institutionId = institutionDto?.id;

  const { handleSubmit, control, errors } = useForm({
    mode: 'onChange',
  });

  const reporterOptions = useMemo(
    () =>
      reporters.map(reporter => ({
        value: reporter,
        label: `${reporter?.firstName} ${reporter?.lastName}`,
      })),
    [reporters]
  );

  useEffect(() => {
    if (changeReporterModalOpen) dispatch(getReporters(institutionId));
  }, [dispatch, institutionId, changeReporterModalOpen]);

  const submitReporter = async ({ reporter }) => {
    const newReporter = reporter?.value;
    if (newReporter) {
      const updateResult = await dispatch(
        changeObligationReporter({ obligationId, user: newReporter })
      );

      if (changeObligationReporter.fulfilled.match(updateResult)) {
        notifySuccess(
          t('datagate.external.users.message.changeObligationReporter')
        );
        dispatch(toggleChangeReporterModal());
        dispatch(getSubmission(submissionId));
      }
    }
  };

  return (
    <Modal
      show={changeReporterModalOpen}
      onClose={() => dispatch(toggleChangeReporterModal())}
      closeOnBlur={!loadingState}
      {...props}
    >
      <ModalCard>
        <form onSubmit={handleSubmit(submitReporter)}>
          <ModalCard.Head>
            <ModalCard.Title>
              {t(
                'datagate.external.users.modal.changeObligationReporter.title',
                { tableName: tableDto?.obligation }
              )}
            </ModalCard.Title>
          </ModalCard.Head>
          <ModalCard.Body>
            <Section className="vertical-padding-md">
              <Row>
                <Column size={12} className="vertical-padding-none">
                  <Field>
                    <Control>
                      <Label required htmlFor="mainUser">
                        {t(
                          'datagate.external.users.modal.changeObligationReporter.form.field.label'
                        )}
                      </Label>
                      <Controller
                        name="reporter"
                        control={control}
                        as={
                          <Select
                            inputId="reporter"
                            menuPortalTarget={document.body}
                            styles={{
                              menuPortal: base => ({ ...base, zIndex: 9999 }),
                            }}
                          />
                        }
                        defaultValue={null}
                        placeholder={t(
                          'datagate.external.users.modal.changeObligationReporter.form.field.placeholder'
                        )}
                        options={reporterOptions}
                        rules={{
                          required: t(
                            'datagate.external.users.modal.changeObligationReporter.form.requiredMessage'
                          ),
                        }}
                      />
                    </Control>
                    <ValidationError name="reporter" errors={errors} />
                  </Field>
                </Column>
              </Row>
            </Section>
          </ModalCard.Body>
          <ModalCard.Foot>
            <Button
              type="button"
              color="primary"
              onClick={() => dispatch(toggleChangeReporterModal())}
              outlined
              id="abandonmainUserChangeButton"
              disabled={loadingState}
            >
              &#215; {t('datagate.external.global.cancel')}
            </Button>
            <Button
              type="submit"
              color="primary"
              disabled={loadingState}
              id="confirmMainUserChangeButton"
            >
              {t('datagate.external.global.confirm')}
            </Button>
          </ModalCard.Foot>
        </form>
      </ModalCard>
    </Modal>
  );
};
